import { useTranslation } from 'react-i18next';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowLeft01Icon, GlobeIcon } from '@hugeicons/core-free-icons';
import { Button } from '@components/Button/Button';
import { useLanguage } from '@hooks/useLanguage';
import { RtlBackIcon } from '@utils/rtlIcons';

export interface PayerShellProps {
  /** When true, show Back button and call onBack when clicked. */
  showBack?: boolean;
  onBack?: () => void;
  children: React.ReactNode;
}

/** Payer flow layout: header (Back + Language), content, footer. No hero. */
export function PayerShell({ showBack = false, onBack, children }: PayerShellProps) {
  const { t } = useTranslation();
  const { language, toggleLanguage } = useLanguage();
  const languageLabel = language === 'ar' ? t('common.english') : t('common.arabic');

  return (
    <div className="min-h-screen w-full bg-surface-web font-sans flex flex-col">
      <header
        className="flex w-full items-center justify-between gap-2 ps-6 pe-6 py-4 sm:ps-10 sm:pe-10 md:ps-12 md:pe-12 shrink-0 bg-surface-web"
        aria-label="Payer navigation"
      >
        {showBack && onBack ? (
          <Button
            variant="ghost"
            size="md"
            className="h-14 p-4 rounded-[16px]"
            onClick={onBack}
            icon={
              <RtlBackIcon>
                <HugeiconsIcon icon={ArrowLeft01Icon} size={20} />
              </RtlBackIcon>
            }
            iconPosition="left"
          >
            {t('common.back')}
          </Button>
        ) : (
          <span />
        )}
        <Button
          variant="ghost"
          size="md"
          className="h-14 p-4 rounded-[16px] shrink-0"
          icon={<HugeiconsIcon icon={GlobeIcon} size={20} />}
          iconPosition="right"
          aria-label={languageLabel}
          onClick={toggleLanguage}
        >
          {languageLabel}
        </Button>
      </header>
      <main className="flex-1 min-h-0 flex flex-col items-center overflow-y-auto px-4 py-6 sm:px-6 md:px-8">
        {children}
      </main>
      <footer
        className="shrink-0 py-4 text-center font-sans text-base font-normal leading-snug text-primary/70 bg-surface-web"
        aria-label="Copyright"
      >
        {t('auth.footer')}
      </footer>
    </div>
  );
}
